import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';
import { IProjectedDelayConfiguration } from '../types/ISettings';

import {
  MESSAGES,
  SCOPES,
  createNotificationHandler,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';

export const projectedDelayApi = createApi({
  reducerPath: 'projectedDelayApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ProjectedDelays'],
  endpoints: (builder) => ({
    getProjectedDelayConfigurations: builder.query<
      IProjectedDelayConfiguration[],
      void
    >({
      query: () => ({
        url: 'Configurations/ProjectedDelays',
        method: 'get',
      }),
      providesTags: ['ProjectedDelays'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_PROJECTED_DELAY,
        MESSAGES.LOAD,
        false
      ),
    }),
    createProjectedDelayConfiguration: builder.mutation<
      IProjectedDelayConfiguration,
      Partial<IProjectedDelayConfiguration>
    >({
      query: (configuration) => ({
        url: 'Configurations/ProjectedDelays',
        method: 'post',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_PROJECTED_DELAY,
          MESSAGES.ADD
        )(configuration, { dispatch, queryFulfilled });

        const { data: newItem } = await queryFulfilled;
        dispatch(
          projectedDelayApi.util.updateQueryData(
            'getProjectedDelayConfigurations',
            undefined,
            (draft) => {
              draft.push(newItem);
            }
          )
        );
      },
    }),
    updateProjectedDelayConfiguration: builder.mutation<
      IProjectedDelayConfiguration,
      IProjectedDelayConfiguration
    >({
      query: (configuration) => ({
        url: `Configurations/ProjectedDelays/${configuration.id}`,
        method: 'put',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_PROJECTED_DELAY,
          MESSAGES.UPDATE
        )(configuration, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;
        dispatch(
          projectedDelayApi.util.updateQueryData(
            'getProjectedDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === updatedItem.id);
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], ...updatedItem };
              }
            }
          )
        );
      },
    }),
    removeProjectedDelayConfiguration: builder.mutation<
      { success: boolean },
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `Configurations/ProjectedDelays/${id}`,
        method: 'delete',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_PROJECTED_DELAY,
          MESSAGES.DELETE
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;
        dispatch(
          projectedDelayApi.util.updateQueryData(
            'getProjectedDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetProjectedDelayConfigurationsQuery,
  useCreateProjectedDelayConfigurationMutation,
  useUpdateProjectedDelayConfigurationMutation,
  useRemoveProjectedDelayConfigurationMutation,
} = projectedDelayApi;

export default projectedDelayApi;
